import {useCallback, useMemo, useState, useSyncExternalStore} from "react";
import {DataGridProProps, GridFilterModel} from "@mui/x-data-grid-pro";

const createFilterModelStore = (key: string) => () => {
	let listeners: (() => void)[] = [];
	const emptyModel = "null";

	return {
		subscribe: (callback: () => void) => {
			if (!key) {
				return () => {};
			}
			listeners.push(callback);
			return () => {
				listeners = listeners.filter(listener => listener !== callback);
			};
		},
		getSnapshot: () => {
			if (!key) {
				return emptyModel;
			}
			try {
				return localStorage.getItem(key) ?? emptyModel;
			} catch (error) {
				return emptyModel;
			}
		},
		getServerSnapshot: () => {
			return emptyModel;
		},
		update: (filterModel: GridFilterModel) => {
			if (!key) {
				return;
			}
			localStorage.setItem(key, JSON.stringify(filterModel));
			listeners.forEach(listener => listener());
		}
	};
};

export const useFilterModel = (key: string) => {
	const [filterModelStore] = useState(
		createFilterModelStore(key)
	);

	const filterModelString = useSyncExternalStore(
		filterModelStore.subscribe,
		filterModelStore.getSnapshot,
		filterModelStore.getServerSnapshot
	);

	const filterModel = useMemo(() => {
		try {
			return (JSON.parse(filterModelString) as GridFilterModel) || undefined;
		} catch (error) {
			return undefined;
		}
	}, [filterModelString]);

	return useMemo(
		() => [filterModel, filterModelStore.update] as const,
		[filterModel, filterModelStore.update]
	);
};

const usePersistedFilterModel = (key: string) => {
	const [filterModel, setFilterModel] = useFilterModel(key);
	const handleFilterModelChange = useCallback<
	NonNullable<DataGridProProps["onFilterModelChange"]>
	>(
		newFilterModel => {
			setFilterModel(newFilterModel);
		},
		[setFilterModel]
	);

	return [filterModel, handleFilterModelChange] as const;
}

export default usePersistedFilterModel;